import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styles from "./ImageViewer.module.css"

type Item = { src: string; label?: string }

export default function ImageViewer({
  items,
  index,
  onClose
}: {
  items: Item[]
  index: number
  onClose: () => void
}) {
  const [i, setI] = useState(index)
  const [scale, setScale] = useState(1)
  const [tx, setTx] = useState(0)
  const [ty, setTy] = useState(0)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const dragging = useRef(false)
  const last = useRef({ x: 0, y: 0 })
  const imgRef = useRef<HTMLImageElement | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const naturals = useRef<{ w: number; h: number }>({ w: 0, h: 0 })
  const preloader = useRef(new Image())

  const current = useMemo(() => items[i], [items, i])

  const fitScale = useCallback(() => {
    const box = boxRef.current
    const { w, h } = naturals.current
    if (!box || !w || !h) return 1
    const cw = box.clientWidth - 80
    const ch = box.clientHeight - 120
    const s = Math.min(cw / w, ch / h)
    return Math.min(0.9, Math.max(0.1, s))
  }, [])

  const clampPan = useCallback((s: number, px: number, py: number) => {
    const box = boxRef.current
    const { w, h } = naturals.current
    if (!box || !w || !h) return { tx: px, ty: py }
    const cw = box.clientWidth
    const ch = box.clientHeight
    const maxX = Math.max(0, (w * s - cw) / 2)
    const maxY = Math.max(0, (h * s - ch) / 2)
    return {
      tx: Math.min(maxX, Math.max(-maxX, px)),
      ty: Math.min(maxY, Math.max(-maxY, py))
    }
  }, [])

  const handleLoaded = useCallback(() => {
    const el = imgRef.current
    if (!el) return
    naturals.current = { w: el.naturalWidth, h: el.naturalHeight }
    const base = fitScale()
    setScale(base)
    setTx(0)
    setTy(0)
    setReady(true)
    setLoading(false)
  }, [fitScale])

  useEffect(() => {
    const onResize = () => {
      if (!ready) return
      const base = fitScale()
      setScale(base)
      setTx(0)
      setTy(0)
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [ready, fitScale])

  const navigateTo = useCallback((newIndex: number) => {
    if (loading || newIndex === i) return

    setLoading(true)
    
    const img = preloader.current
    img.onload = () => {
      setI(newIndex)
      setReady(false)
    }
    img.onerror = () => {
      setLoading(false)
    }
    img.src = items[newIndex].src
  }, [loading, i, items])

  const next = useCallback(() => navigateTo((i + 1) % items.length), [i, items.length, navigateTo])
  const prev = useCallback(() => navigateTo((i - 1 + items.length) % items.length), [i, items.length, navigateTo])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowRight") next()
      else if (e.key === "ArrowLeft") prev()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [next, prev, onClose])

  const onWheel: React.WheelEventHandler = (e) => {
    if (!ready || loading) return
    e.preventDefault()
    const delta = -e.deltaY
    const base = fitScale()
    const ns = Math.min(5, Math.max(base, scale + delta * 0.0015))
    const c = clampPan(ns, tx, ty)
    setScale(ns)
    if (ns === base) {
      setTx(0)
      setTy(0)
    } else {
      setTx(c.tx)
      setTy(c.ty)
    }
  }

  const onMouseDown: React.MouseEventHandler = (e) => {
    if (!ready || loading) return
    if (scale <= fitScale()) return
    dragging.current = true
    last.current = { x: e.clientX, y: e.clientY }
  }

  const onMouseMove: React.MouseEventHandler = (e) => {
    if (!dragging.current) return
    const dx = e.clientX - last.current.x
    const dy = e.clientY - last.current.y
    last.current = { x: e.clientX, y: e.clientY }
    const c = clampPan(scale, tx + dx, ty + dy)
    setTx(c.tx)
    setTy(c.ty)
  }

  const onMouseUp = () => {
    dragging.current = false
  }

  const zoomOut = useCallback(() => {
    if (!ready || loading) return
    const base = fitScale()
    const ns = Math.max(base, scale - 0.25)
    if (ns === base) {
      setScale(ns)
      setTx(0)
      setTy(0)
    } else {
      const c = clampPan(ns, tx, ty)
      setScale(ns)
      setTx(c.tx)
      setTy(c.ty)
    }
  }, [ready, loading, scale, tx, ty, fitScale, clampPan])
  
  const zoomIn = useCallback(() => {
    if (!ready || loading) return
    const ns = Math.min(5, scale + 0.25)
    const c = clampPan(ns, tx, ty)
    setScale(ns)
    setTx(c.tx)
    setTy(c.ty)
  }, [ready, loading, scale, tx, ty, clampPan])

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div ref={boxRef} className={styles.center} onClick={(e) => e.stopPropagation()}>
        <div className={styles.topbar}>
          <button className={styles.topbarBtn} onClick={onClose} aria-label="Voltar">Voltar</button>
          <div className={styles.topbarTitle}>{current.label ?? ""}</div>
          <div className={styles.topbarRight} />
        </div>

        <img
          ref={imgRef}
          src={current.src}
          alt={current.label ?? ""}
          className={styles.img}
          draggable={false}
          onLoad={handleLoaded}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          style={{
            transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`,
            visibility: ready ? "visible" : "hidden"
          }}
        />
        
        <button 
          className={`${styles.navBtn} ${styles.navLeft}`} 
          onClick={prev} 
          aria-label="Anterior"
          disabled={loading}
        >
          <span className={styles.navIcon}>&larr;</span>
        </button>
        <button 
          className={`${styles.navBtn} ${styles.navRight}`} 
          onClick={next} 
          aria-label="Próxima"
          disabled={loading}
        >
          <span className={styles.navIcon}>&rarr;</span>
        </button>

        <div className={styles.zoombar} onClick={e => e.stopPropagation()}>
          <button
            className={styles.btn}
            onClick={zoomOut}
            aria-label="Diminuir zoom"
            disabled={loading}
          >-</button>
          <div className={styles.zoom}>
            {Math.round((scale / Math.max(1e-6, fitScale())) * 100)}%
          </div>
          <button
            className={styles.btn}
            onClick={zoomIn}
            aria-label="Aumentar zoom"
            disabled={loading}
          >+</button>
        </div>
      </div>
    </div>
  )
}